<section class="bg-star-gray-600 pt-[40px] pb-[60px] lg:pt-[64px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h1 class="text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[44px]">
      AML Compliance Software for Crypto Businesses
    </h1>
    <p class="mt-[20px] max-w-[760px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer is a crypto AML platform that puts four AML data providers behind one interface.
      Screen addresses, keep wallets under watch, send alerts to the people who act on them, give
      your team roles, and reach the same data from your own systems through an API.
    </p>
    <a
      [href]="createAccountHref()"
      target="_blank"
      class="btn btn-white btn-with-arrow mt-[28px] min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
    >
      <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
      <mat-icon
        svgIcon="second-arrow-right"
        class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
      ></mat-icon>
    </a>
    <p class="mt-[16px] max-w-[620px] text-[14px] leading-[1.6] text-white/50">
      The platform is used from a business account. Commercial terms follow your volumes, and yours
      become visible in the account once you register.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      An aggregator, not another single-source tool
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Most AML software sells you one company's view of the blockchain. That view is never complete:
      providers build their datasets differently, and the address that matters to you may be known
      to the one you did not buy. Working around that means signing several contracts, holding
      several keys, normalising several response formats, and then deciding for yourself what to do
      when two sources disagree.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer takes that work off your side. The platform aggregates four AML data providers,
      including BitOK, Global Ledger and Beosin, into a single interface with one account behind it
      — so the breadth of several sources is available to your compliance team without a procurement
      project for each one.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      The workflows the platform covers
    </h2>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-2">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Screening before a decision</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Put an address in and get its AML risk picture back, at the depth the case calls for:
          incoming funds, outgoing funds or the full transaction history. Detail on this workflow
          lives on the
          <a routerLink="/wallet-screening" class="text-white underline underline-offset-4"
            >crypto wallet screening page</a
          >.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">
          Watching what you already accepted
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Wallets can be added by hand or handed to automated real-time monitoring, so a
          counterparty that changed behaviour after onboarding does not stay unnoticed until an
          auditor finds it.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Alerts that reach a person</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Notifications go out the moment suspicious activity is detected — in the dashboard and in
          Telegram, group chats included, which is where most compliance teams already talk.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">
          Reports that outlive the decision
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Wallet compliance reports are generated and kept, so a formal request or an internal
          review can be answered with what was actually known at the time.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Software a team can work in
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Compliance is rarely one person with a browser tab. AMLOfficer is built around shared work:
      you invite colleagues, assign roles, and distribute responsibility for wallets instead of
      routing everything through whoever holds the login. Investigations are shared rather than
      re-done, and the history behind a wallet is visible to everyone entitled to see it.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      That matters most at the moments compliance teams dread: a regulator or a law-enforcement
      request arrives with a deadline, and the answer has to be assembled from what the platform
      recorded — not from five people's memories and a spreadsheet.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      How it meets the systems you already run
    </h2>
    <div class="mt-[28px] flex flex-col gap-[40px] lg:flex-row lg:items-center">
      <div class="flex-1">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">Work in the dashboard</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          The interface is the whole product for most teams: wallets, their reports, alerts and the
          people who handle them, in one place.
        </p>
        <h3 class="mt-[24px] text-[19px] font-bold leading-[1.3] text-white">Work in Telegram</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Reports and alerts can be generated and received in a few taps, so the compliance loop
          closes where operational conversations already happen.
        </p>
        <h3 class="mt-[24px] text-[19px] font-bold leading-[1.3] text-white">
          Work through the API
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          One integration path keeps your people in the AMLOfficer interface while reports flow to
          where they are needed; the other brings AML data into your own systems in real time, for
          full control and advanced use cases.
        </p>
      </div>
      <div class="flex flex-1 justify-center">
        <img
          src="./images/aml-api-desktop.png"
          alt=""
          width="786"
          height="440"
          loading="lazy"
          class="w-full max-w-[392px] rounded-[10px] object-cover object-center"
        />
      </div>
    </div>
    <p class="mt-[28px] text-[16px] leading-[1.8] text-white/80">
      One boundary is worth stating plainly: AMLOfficer works on the blockchain side of compliance.
      Verifying the identity of a customer is a separate part of your stack, and this platform does
      not claim to replace it.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Which businesses run on it
    </h2>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-2">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">OTC desks</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Where a single settlement is large enough that a counterparty's transaction history
          deserves to be examined before, not after, the trade.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">
          VASPs and virtual asset platforms
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Where wallet activity has to be watched continuously and formal information requests have
          to be answered in a structured, documented way.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">Crypto exchangers</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Where every incoming asset is someone else's history, and preventive controls have to run
          at the speed of the deposit flow.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">
          Compliance officers and finance teams
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Where oversight is spread across clients, merchants and services, and the practical
          problem is that the data lives in too many places to act on quickly.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      What the choice usually comes down to
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Comparing AML compliance software gets easier once the marketing is stripped away. Four
      questions decide most of it.
    </p>
    <ul class="mt-[24px] flex flex-col gap-[16px]">
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Whose data is behind an answer.</span> One provider's dataset,
          or several? AMLOfficer names its sources and aggregates four of them.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Whether the tool fits a team.</span> Roles, invitations and
          shared investigations are the difference between software a department uses and a licence
          one analyst holds.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Whether it reaches your systems.</span> A platform that cannot
          be called from your product turns compliance into manual work at exactly the volume where
          manual work fails.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Whether the record survives.</span> Reports and alert history
          are what you produce when someone asks why a transaction was accepted eleven months
          ago.</span
        >
      </li>
    </ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Getting started
    </h2>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-3">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">1. Open a business account</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Registration is the first step; the dashboard and your terms become available inside the
          account.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">2. Add wallets and people</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Screen the addresses you care about, put the ones worth watching under monitoring, and
          bring in the people who will handle them.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">3. Connect your systems</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Route alerts into Telegram and, when you are ready, move the checks your product makes
          onto the API.
        </p>
      </div>
    </div>
    <p class="mt-[24px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer is operated by STAR COMPLIANCE CYBER RISK MANAGEMENT SERVICES L.L.C, licence
      1135542, based at One Central, Dubai World Trade Centre, Dubai, UAE.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      AML compliance software: common questions
    </h2>
    <div class="mt-[28px] flex flex-col gap-[24px]">
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          What is AML compliance software for crypto?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          It is the tooling a business uses to assess crypto addresses and transactions for AML
          risk, keep watching the ones it has accepted, alert the responsible people when something
          changes, and keep the record of both. AMLOfficer covers those workflows in one platform,
          with four AML data providers behind the answers.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">Who is the platform for?</h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Crypto businesses that receive or send assets on behalf of others — OTC desks, VASPs and
          virtual asset platforms, exchangers — and the compliance and finance functions that
          oversee them.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Why aggregate providers instead of choosing one?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Because providers do not see the same things. An address one dataset treats as ordinary
          can be flagged in another, and with a single source you never learn that a second opinion
          existed. Aggregation puts that difference in front of your team.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Can several colleagues use one account?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Yes. Team members are invited into the business account and given roles, so
          responsibilities are distributed and investigations are shared instead of duplicated.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Does it work only through the interface?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          No. There is an API as well, on two levels: one that keeps your people working in the
          interface while reports reach the systems that need them, and one that embeds the AML data
          itself into your product.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[80px] lg:pb-[110px]">
  <div
    class="mx-auto flex max-w-[1000px] flex-col items-start gap-[20px] rounded-[10px] border border-solid border-white/20 px-[20px] py-[32px] lg:flex-row lg:items-center lg:justify-between lg:px-[40px]"
  >
    <div>
      <h2 class="text-[22px] font-medium leading-[1.3] text-white lg:text-[26px]">
        Put your compliance work in one place
      </h2>
      <p class="mt-[10px] max-w-[520px] text-[15px] leading-[1.7] text-white/70">
        Open a business account and start with the wallets that already worry you.
      </p>
    </div>
    <a
      [href]="createAccountHref()"
      target="_blank"
      class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[280px] rounded-[8px] font-harmony font-semibold leading-[18px]"
    >
      <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
      <mat-icon
        svgIcon="second-arrow-right"
        class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
      ></mat-icon>
    </a>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
